<div class="container">
  <sca-module-title class="title-test">
    <h1>Multilanguage Playground</h1>
    <span scaModuleTitlePost>Testing exhaustivo de @argo/multilanguage - {{ coverage }}% de cobertura</span>
  </sca-module-title>

  <!-- Estado y Fase -->
  <section class="card">
    <h2>Estado de Carga (CommonPhase)</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      MultilanguageStore utiliza CommonPhase para gestionar el ciclo de vida de carga de idiomas
    </sca-alert>
    
    <div class="status-grid">
      <sca-card-workshop [selected]="multilang.phase() === CommonPhase.IDLE">
        <header scaCardWorkshopHeader>
          <sca-badge variant="default" [rounded]="false">Sin cargar</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Estado inicial</p>
        <p scaCardWorkshopAddress>IDLE</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="multilang.phase() === CommonPhase.LOADING">
        <header scaCardWorkshopHeader>
          <sca-badge variant="warning" [rounded]="false">Cargando...</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Cargando idiomas</p>
        <p scaCardWorkshopAddress>LOADING</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="multilang.phase() === CommonPhase.LOADED">
        <header scaCardWorkshopHeader>
          <sca-badge variant="success" [rounded]="false">Cargado OK</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Idiomas disponibles</p>
        <p scaCardWorkshopAddress>LOADED</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="multilang.phase() === CommonPhase.ERROR">
        <header scaCardWorkshopHeader>
          <sca-badge variant="error" [rounded]="false">Error</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Falló la carga</p>
        <p scaCardWorkshopAddress>ERROR</p>
      </sca-card-workshop>
    </div>
    
    @if (multilang.error()) {
      <sca-alert variant="error" style="margin-top: 1rem;">
        <sca-icon icon="error" size="24px" [weight]="300" left />
        <strong>Error:</strong> {{ multilang.error() }}
      </sca-alert>
    }
  </section>

  <!-- Computed Properties -->
  <section class="card">
    <h2>Computed Properties</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Propiedades reactivas computadas del MultilanguageStore
    </sca-alert>
    
    <table class="properties-table">
      <thead>
        <tr>
          <th>Propiedad</th>
          <th>Tipo</th>
          <th>Valor Actual</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>language()</code></td>
          <td>Computed&lt;string&gt;</td>
          <td><span class="value-enum">{{ multilang.language() }}</span></td>
          <td>Idioma activo actual</td>
        </tr>
        <tr>
          <td><code>supportedLangs()</code></td>
          <td>Signal&lt;string[]&gt;</td>
          <td><span class="value-number">{{ multilang.supportedLangs().length }}</span> idiomas: {{ multilang.supportedLangs().join(', ') }}</td>
          <td>Lista de idiomas disponibles</td>
        </tr>
        <tr>
          <td><code>languagesLoaded()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="multilang.languagesLoaded() ? 'value-true' : 'value-false'">{{ multilang.languagesLoaded() }}</span></td>
          <td>¿Todos los idiomas cargados?</td>
        </tr>
        <tr>
          <td><code>isLoading()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="multilang.isLoading() ? 'value-true' : 'value-false'">{{ multilang.isLoading() }}</span></td>
          <td>¿Cargando traducciones?</td>
        </tr>
        <tr>
          <td><code>isLoaded()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="multilang.isLoaded() ? 'value-true' : 'value-false'">{{ multilang.isLoaded() }}</span></td>
          <td>¿Traducciones cargadas?</td>
        </tr>
        <tr>
          <td><code>isError()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="multilang.isError() ? 'value-warning' : 'value-false'">{{ multilang.isError() }}</span></td>
          <td>¿Error al cargar traducciones?</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- Idioma Actual y Selector -->
  <section class="card">
    <h2>Idioma Actual y Selector</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Selecciona un idioma para cambiar las traducciones dinámicamente
    </sca-alert>

    <sca-alert variant="success" style="margin-bottom: 1.5rem;">
      <strong>Idioma actual:</strong> {{ getFlagEmoji(multilang.language()) }} {{ getLanguageName(multilang.language()) }} ({{ multilang.language() }})
    </sca-alert>

    <div class="utilities-grid">
      @for (lang of multilang.supportedLangs(); track lang) {
        <sca-card-workshop [selected]="multilang.language() === lang" (click)="changeLanguage(lang)">
          <header scaCardWorkshopHeader>
            <sca-icon icon="translate" size="24" [weight]="400" />
          </header>
          <p scaCardWorkshopTitle>{{ getFlagEmoji(lang) }} {{ getLanguageName(lang) }}</p>
          <p scaCardWorkshopAddress>
            <sca-badge [variant]="multilang.language() === lang ? 'success' : 'default'">
              {{ lang }}
            </sca-badge>
          </p>
        </sca-card-workshop>
      }
    </div>
    
    @if (useResult) {
      <sca-alert [variant]="useResult.includes('✅') ? 'success' : 'error'" style="margin-top: 1rem;">
        {{ useResult }}
      </sca-alert>
    }
  </section>

  <!-- Métodos Públicos -->
  <section class="card">
    <h2>Métodos Principales del Store</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      API pública del MultilanguageStore
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="play_arrow" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>initializeLanguage()</p>
        <p scaCardWorkshopAddress>Inicializa idioma desde localStorage o navegador</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="autorenew" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>use(language)</p>
        <p scaCardWorkshopAddress>Cambia el idioma activo con best match automático</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="folder_open" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>loadLanguages(path)</p>
        <p scaCardWorkshopAddress>Carga traducciones desde ruta dinámica o array de rutas</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="storage" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>getBrowserLang()</p>
        <p scaCardWorkshopAddress>Detecta el idioma preferido del navegador</p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Traducciones en Tiempo Real -->
  <section class="card">
    <h2>Traducciones en Tiempo Real</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Demostración del pipe 'translate' de ngx-translate - Las traducciones se actualizan automáticamente al cambiar el idioma
    </sca-alert>

    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="home" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>home.title</p>
        <p scaCardWorkshopAddress>{{ 'home.title' | translate }}</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="email" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>home.subtitle</p>
        <p scaCardWorkshopAddress>{{ 'home.subtitle' | translate }}</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="person" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>common.username</p>
        <p scaCardWorkshopAddress>{{ 'common.username' | translate }}</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="lock" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>common.password</p>
        <p scaCardWorkshopAddress>{{ 'common.password' | translate }}</p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Algoritmo de Best Match -->
  <section class="card">
    <h2>Algoritmo: findBestMatch()</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Algoritmo inteligente de coincidencia de idiomas:
      <ol style="margin: 0.5rem 0 0 1.5rem;">
        <li>Busca coincidencia exacta (ej: 'es-ES' en ['es-ES', 'en'])</li>
        <li>Busca por código base (ej: 'es-ES' → 'es' en ['es', 'en'])</li>
        <li>Devuelve idioma por defecto si no hay coincidencia</li>
      </ol>
    </sca-alert>

    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="search" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Coincidencia Exacta</p>
        <p scaCardWorkshopAddress>Primera prioridad: busca el idioma exacto en la lista de soportados</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="code" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Código Base</p>
        <p scaCardWorkshopAddress>Segunda prioridad: busca por prefijo (es-ES → es)</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="flag" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Idioma por Defecto</p>
        <p scaCardWorkshopAddress>Última opción: devuelve el idioma por defecto configurado</p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Interceptor HTTP -->
  <section class="card">
    <h2>Multilanguage Interceptor (HTTP)</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      El interceptor añade automáticamente headers de idioma a todas las peticiones HTTP
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="settings" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Locale Header</p>
        <p scaCardWorkshopAddress>
          <sca-badge variant="success">{{ multilang.language() }}</sca-badge><br>
          Se añade a cada petición HTTP
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="language" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Accept-Language</p>
        <p scaCardWorkshopAddress>
          <sca-badge variant="success">{{ multilang.language() }}</sca-badge><br>
          Header estándar HTTP
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="block" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Excepciones</p>
        <p scaCardWorkshopAddress>No añade headers a URLs con "login" o "assets/i18n"</p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- NGX-Translate Service -->
  <section class="card">
    <h2>NGX-Translate Service</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Acceso directo al servicio de ngx-translate: <code>multilang.ngxTranslateService</code>
    </sca-alert>

    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="public" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>getBrowserLang()</p>
        <p scaCardWorkshopAddress>Detecta el idioma preferido del navegador del usuario</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="flash_on" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>instant(key)</p>
        <p scaCardWorkshopAddress>Obtiene traducción de forma síncrona sin Observable</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="stream" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>stream(key)</p>
        <p scaCardWorkshopAddress>Stream Observable que emite traducciones reactivamente</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="refresh" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>reloadLang(lang)</p>
        <p scaCardWorkshopAddress>Fuerza la recarga del archivo JSON de un idioma</p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- LocalStorage -->
  <section class="card">
    <h2>LocalStorage Persistence</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      El idioma seleccionado se guarda automáticamente en localStorage
    </sca-alert>

    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="storage" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Key en localStorage</p>
        <p scaCardWorkshopAddress>
          <code>'language'</code>
        </p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="true">
        <header scaCardWorkshopHeader>
          <sca-icon icon="save" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Valor actual</p>
        <p scaCardWorkshopAddress>
          <sca-badge variant="success">{{ getStoredLanguage() }}</sca-badge>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="autorenew" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Persistencia automática</p>
        <p scaCardWorkshopAddress>Se actualiza al cambiar el idioma con use()</p>
      </sca-card-workshop>
    </div>
  </section>
</div>
